import { Component, OnInit, ViewChild, DestroyRef, inject, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldService } from '../../../../core/services/field.service';
import { CustomerService } from '../../../../core/services/customer.service';
import { Field, CreateFieldDto } from '../../../../core/models/field.model';
import { Customer } from '../../../../core/models/customer.model';
import { LocationPanelComponent } from '../../../../shared/components/location-panel/location-panel.component';
import { BoundaryEditorComponent } from '../../../../shared/components/boundary-editor/boundary-editor.component';
import { FieldMapsCardComponent } from '../../../../shared/components/field-maps-card/field-maps-card.component';
import { CustomerQuickAddComponent } from '../../../../shared/components/customer-quick-add/customer-quick-add.component';
import { AgremoService } from '../../../../core/services/agremo.service';
import { computeCentroid } from '../../../../core/utils/boundary-utils';
import { geoJsonPolygonToCoords } from '../../../../core/utils/geojson-utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-field-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, LocationPanelComponent, BoundaryEditorComponent, FieldMapsCardComponent, CustomerQuickAddComponent, MaxLengthDirective, CharCounterDirective],
  templateUrl: './field-form.component.html',
  styleUrls: ['./field-form.component.scss']
})
export class FieldFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);
  private agremoService = inject(AgremoService);
  private destroyRef = inject(DestroyRef);

  form!: FormGroup;
  isEditMode = false;
  fieldId: string | null = null;
  isSaving = false;
  isFieldLoaded = false;

  @ViewChild('customerQuickAdd') customerQuickAdd!: CustomerQuickAddComponent;

  // Customer options for dropdown (computed signal stays reactive with stable references)
  customerOptions = computed(() => this.customerService.getCustomerOptions());

  // Coordinate display mode (kept for backward compatibility)
  coordinateDisplayMode: 'decimal' | 'dms' = 'decimal';

  // Location panel input values
  initialLatitude: number | null = null;
  initialLongitude: number | null = null;
  initialAddress: string | null = null;

  // Boundary editor state
  boundaryGeojson: any | null = null;
  boundaryKml: string | null = null;
  isBoundaryExpanded = false;

  // Agremo sync state
  isAgremoConfigured = this.agremoService.isConfigured;
  agremoFieldId: string | null = null;
  isSyncingAgremo = false;
  agremoSyncSuccess = false;
  agremoSyncError: string | null = null;
  boundaryChangedSinceSync = false;
  private ignoreNextBoundaryChange = false;

  // Common crop types for dropdown
  cropTypes = [
    'Corn',
    'Soybeans',
    'Wheat',
    'Cotton',
    'Rice',
    'Sorghum',
    'Alfalfa',
    'Hay',
    'Barley',
    'Oats',
    'Apple Trees',
    'Citrus Trees',
    'Grapes',
    'Vegetables',
    'Other'
  ];

  // US States for dropdown
  states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ];

  /**
   * Whether the field has coordinates set (needed to enable boundary editor).
   * Requires both lat and lon to be present (unless boundary already exists).
   */
  get hasCoordinates(): boolean {
    const lat = this.form?.get('latitude')?.value;
    const lon = this.form?.get('longitude')?.value;
    return ((lat != null && lat !== '') && (lon != null && lon !== '')) || !!this.boundaryGeojson;
  }

  /** Effective latitude for boundary editor (handles 0 as valid coordinate) */
  get effectiveLatitude(): number | null {
    const val = this.form?.get('latitude')?.value;
    return val != null && val !== '' ? val : this.initialLatitude;
  }

  /** Effective longitude for boundary editor (handles 0 as valid coordinate) */
  get effectiveLongitude(): number | null {
    const val = this.form?.get('longitude')?.value;
    return val != null && val !== '' ? val : this.initialLongitude;
  }

  /**
   * Whether Agremo sync is already up-to-date (synced and boundary unchanged).
   */
  get isAgremoSynced(): boolean {
    return !!this.agremoFieldId && !this.boundaryChangedSinceSync;
  }

  ngOnInit(): void {
    this.initForm();
    this.checkEditMode();
    this.checkQueryParams();
    this.agremoService.checkStatus();
  }

  private checkQueryParams(): void {
    // Check for customerId query param (from customer details "Add Field" button)
    const customerId = this.route.snapshot.queryParamMap.get('customerId');
    if (customerId && !this.isEditMode) {
      this.form.patchValue({ customerId });
    }
  }

  // Whether "Other" is selected for crop type (shows custom input)
  get isOtherCropType(): boolean {
    return this.form?.get('cropType')?.value === 'Other';
  }

  private initForm(): void {
    this.form = this.fb.group({
      customerId: [''],
      name: ['', [Validators.required, Validators.minLength(2)]],
      acreage: ['', [Validators.required, Validators.min(0.1)]],
      cropType: ['', Validators.required],
      customCropType: [''],
      latitude: ['', [Validators.min(-90), Validators.max(90)]],
      longitude: ['', [Validators.min(-180), Validators.max(180)]],
      address: [''],
      city: [''],
      state: [''],
      notes: ['']
    });

    // Clear customCropType value and errors when switching away from "Other"
    this.form.get('cropType')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      if (value !== 'Other') {
        this.form.get('customCropType')?.reset();
      }
    });
  }

  private checkEditMode(): void {
    this.fieldId = this.route.snapshot.paramMap.get('id');
    if (this.fieldId) {
      this.isEditMode = true;
      this.loadField(this.fieldId);
    }
  }

  private async loadField(id: string): Promise<void> {
    // Fetch full field data from API to get boundaryGeojson
    const field = await this.fieldService.fetchFieldById(id);
    if (field) {
      // If the stored crop type isn't in the predefined list, treat it as "Other"
      // and prefill the custom input so the user can save without re-entering.
      // Legacy records with literal "Other" will show the custom input empty —
      // this is intentional so users specify the actual crop type on next edit.
      const isKnownCrop = this.cropTypes.includes(field.cropType);
      this.form.patchValue({
        customerId: field.customerId || '',
        name: field.name,
        acreage: field.acreage,
        cropType: isKnownCrop ? field.cropType : 'Other',
        customCropType: isKnownCrop ? '' : field.cropType,
        latitude: field.latitude,
        longitude: field.longitude,
        address: field.address || '',
        city: field.city || '',
        state: field.state || '',
        notes: field.notes || ''
      });

      // Set initial values for location panel
      this.initialLatitude = field.latitude ?? null;
      this.initialLongitude = field.longitude ?? null;
      this.initialAddress = field.address || null;

      // Track Agremo sync status
      this.agremoFieldId = field.agremoFieldId || null;

      // Load existing boundary for boundary editor
      if (field.boundaryGeojson) {
        this.ignoreNextBoundaryChange = true;
        this.boundaryGeojson = field.boundaryGeojson;
        this.isBoundaryExpanded = true;

        // Edge case: if boundaryGeojson exists but lat/lon are null,
        // compute centroid and set as initial coordinates
        if (field.latitude == null && field.longitude == null) {
          const coords = geoJsonPolygonToCoords(field.boundaryGeojson);
          if (coords.length > 0) {
            const centroid = computeCentroid(coords);
            this.initialLatitude = centroid.lat;
            this.initialLongitude = centroid.lng;
          }
        }
      }
      this.isFieldLoaded = true;
    } else {
      // Field not found, redirect to list
      this.router.navigate(['/logbook/fields']);
    }
  }

  toggleCoordinateDisplay(): void {
    this.coordinateDisplayMode = this.coordinateDisplayMode === 'decimal' ? 'dms' : 'decimal';
  }

  // Convert DMS input to decimal
  parseDMSToDecimal(dms: string): number | null {
    // Simple pattern: accepts "32°46'36.12"N" or "32 46 36.12 N" formats
    const pattern = /^(-?\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSEW])?$/i;
    const match = dms.trim().match(pattern);

    if (!match) {
      return null;
    }

    let degrees = parseFloat(match[1]);
    const minutes = parseFloat(match[2]);
    const seconds = parseFloat(match[3]);
    const direction = match[4]?.toUpperCase();

    let decimal = Math.abs(degrees) + (minutes / 60) + (seconds / 3600);

    if (direction === 'S' || direction === 'W' || degrees < 0) {
      decimal = -decimal;
    }

    return decimal;
  }

  /**
   * Handles coordinate changes from the LocationPanelComponent.
   * Updates form values with the new coordinates and address information.
   */
  onCoordinatesChanged(event: {
    lat: number | null;
    lon: number | null;
    address: string | null;
    city: string | null;
    state: string | null;
  }): void {
    this.form.patchValue({
      latitude: event.lat,
      longitude: event.lon,
      address: event.address || '',
      city: event.city || '',
      state: event.state || ''
    });
  }

  /**
   * Handles boundary changes from the BoundaryEditorComponent.
   * Stores the GeoJSON boundary for inclusion in form submission.
   */
  onBoundaryChanged(geojson: any): void {
    this.boundaryGeojson = geojson;

    // Skip change tracking for the initial echo when loading existing boundary
    if (this.ignoreNextBoundaryChange) {
      this.ignoreNextBoundaryChange = false;
      return;
    }

    this.boundaryChangedSinceSync = true;
    this.agremoSyncSuccess = false;
  }

  /**
   * Handles acreage changes from the BoundaryEditorComponent.
   * Patches the form's acreage control with the calculated value.
   * User can manually overwrite afterward.
   */
  onAcreageChanged(acreage: number): void {
    this.form.patchValue({ acreage });
  }

  /**
   * Handles original file content changes from the BoundaryEditorComponent.
   * Stores the original KML file content for boundary_kml storage.
   */
  onOriginalFileChanged(fileContent: string | null): void {
    this.boundaryKml = fileContent;
  }

  /**
   * Syncs the current field to Agremo for crop analysis.
   * Requires a boundary to be defined on the field.
   */
  async syncToAgremo(): Promise<void> {
    if (!this.fieldId || !this.boundaryGeojson) {
      return;
    }

    this.isSyncingAgremo = true;
    this.agremoSyncSuccess = false;
    this.agremoSyncError = null;

    try {
      const result = await this.fieldService.syncToAgremo(this.fieldId);
      this.agremoFieldId = result.agremoFieldId || this.agremoFieldId;
      this.agremoSyncSuccess = true;
      this.boundaryChangedSinceSync = false;
    } catch (error: any) {
      this.agremoSyncError = error?.error?.error || error?.message || 'Failed to sync to Agremo';
    } finally {
      this.isSyncingAgremo = false;
    }
  }

  openCustomerQuickAdd(): void {
    this.customerQuickAdd.open();
  }

  onCustomerCreated(customer: Customer): void {
    // Select the newly created customer in the dropdown
    this.form.patchValue({ customerId: customer.id });
  }

  async onSubmit(): Promise<void> {
    // Trim name before validation so whitespace-only input is caught
    const nameControl = this.form.get('name');
    if (nameControl?.value) {
      nameControl.setValue(nameControl.value.trim());
    }

    // Validate custom crop type when "Other" is selected
    if (this.form.get('cropType')?.value === 'Other' && !this.form.get('customCropType')?.value?.trim()) {
      this.form.get('customCropType')?.markAsTouched();
      this.form.get('customCropType')?.setErrors({ required: true });
      return;
    }

    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving = true;

    const formData = this.form.getRawValue();
    const effectiveCropType = formData.cropType === 'Other' && formData.customCropType?.trim()
      ? formData.customCropType.trim()
      : formData.cropType;
    const dto: CreateFieldDto = {
      name: formData.name,
      acreage: parseFloat(formData.acreage),
      cropType: effectiveCropType,
      customerId: formData.customerId || undefined,
      latitude: formData.latitude != null && formData.latitude !== '' ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude != null && formData.longitude !== '' ? parseFloat(formData.longitude) : null,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      notes: formData.notes || undefined,
      boundaryGeojson: this.boundaryGeojson || undefined,
      boundaryKml: this.boundaryKml || undefined
    };

    try {
      if (this.isEditMode && this.fieldId) {
        await this.fieldService.updateField(this.fieldId, dto);
      } else {
        await this.fieldService.createField(dto);
      }

      // Refresh field options to ensure the new/updated field is in cache
      await this.fieldService.refreshFieldOptions();

      this.router.navigate(['/logbook/fields']);
    } catch (error) {
      console.error('Error saving field:', error);
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/logbook/fields']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  openInMaps(): void {
    const lat = this.form.get('latitude')?.value;
    const lng = this.form.get('longitude')?.value;
    if (lat && lng) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, '_blank');
    }
  }
}
